<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-container bon-retour-modal" @click.stop>
      <div class="modal-header">
        <h3>📋 Nouveau Bon de Retour</h3>
        <button @click="closeModal" class="close-btn">×</button>
      </div>

      <div class="modal-body">
        <!-- Sélection Technicien -->
        <div class="section">
          <h4>👨‍🔧 Technicien</h4>
          <select v-model="selectedTechnicien" class="form-select">
            <option value="">Sélectionner un technicien</option>
            <option v-for="tech in techniciens" :key="tech._id" :value="tech._id">
              {{ tech.prenom }} {{ tech.nom }}
            </option>
          </select>
        </div>

        <!-- Liste des produits -->
        <div class="section" v-if="selectedTechnicien">
          <h4>📦 Produits à retourner</h4>
          <div v-if="loading" class="loading">Chargement des produits...</div>
          <div v-else-if="produits.length === 0" class="no-products">
            Aucun produit disponible pour ce technicien
          </div>
          <div v-else class="products-list">
            <div class="select-all">
              <label>
                <input type="checkbox" @change="toggleSelectAll" :checked="allSelected">
                Sélectionner tout ({{ selectedProducts.length }}/{{ produits.length }})
              </label>
            </div>
            <div v-for="produit in produits" :key="produit._id" class="product-item">
              <label class="product-checkbox">
                <input 
                  type="checkbox" 
                  :value="produit._id" 
                  v-model="selectedProducts"
                >
                <div class="product-info">
                  <div class="product-main">
                    <strong>{{ produit.referenceInterne }}</strong>
                    <span class="designation">{{ produit.designation }}</span>
                  </div>
                  <div class="product-details">
                    <span class="category">{{ produit.categorie }}</span>
                    <span class="status">{{ produit.statut }}</span>
                    <span class="imei" v-if="produit.imei">{{ produit.imei }}</span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- Commentaires -->
        <div class="section" v-if="selectedProducts.length > 0">
          <h4>📝 Commentaires</h4>
          <textarea 
            v-model="commentaires" 
            placeholder="Raison du retour, état des produits..."
            rows="3"
          ></textarea>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="closeModal" class="btn btn-secondary">Annuler</button>
        <button 
          @click="exportPDF" 
          :disabled="selectedProducts.length === 0 || exporting"
          class="btn btn-primary"
        >
          <i class="fas fa-file-pdf"></i>
          {{ exporting ? 'Export en cours...' : 'Exporter PDF' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export default {
  name: 'BonRetourModal',
  emits: ['close', 'created'],
  setup(props, { emit }) {
    const selectedTechnicien = ref('')
    const techniciens = ref([])
    const produits = ref([])
    const selectedProducts = ref([])
    const commentaires = ref('')
    const loading = ref(false)
    const exporting = ref(false)

    const allSelected = computed(() => {
      return produits.value.length > 0 && selectedProducts.value.length === produits.value.length
    })

    // Charger les techniciens
    const loadTechniciens = async () => {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('/api/users?role=Technicien', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.ok) {
          const data = await response.json()
          techniciens.value = data.users || []
        }
      } catch (error) {
        console.error('Erreur chargement techniciens:', error)
      }
    }

    // Charger les produits du technicien
    const loadProduits = async () => {
      if (!selectedTechnicien.value) return
      
      loading.value = true
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`/api/products?technicien=${selectedTechnicien.value}&statut=affecte_technicien,en_cours,installe`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.ok) {
          const data = await response.json()
          produits.value = data.products || []
          selectedProducts.value = []
        }
      } catch (error) {
        console.error('Erreur chargement produits:', error)
      } finally {
        loading.value = false
      }
    }

    const toggleSelectAll = () => {
      if (allSelected.value) {
        selectedProducts.value = []
      } else {
        selectedProducts.value = produits.value.map(p => p._id)
      }
    }

    const exportPDF = async () => {
      if (selectedProducts.value.length === 0) return

      exporting.value = true
      try {
        // Créer le mouvement de retour
        await createReturnMovement()
        
        // Générer le PDF
        await generatePDF()
        
        emit('created')
        closeModal()
      } catch (error) {
        console.error('Erreur export PDF:', error)
        alert('Erreur lors de l\'export PDF')
      } finally {
        exporting.value = false
      }
    }

    const createReturnMovement = async () => {
      const token = localStorage.getItem('token')
      const selectedProduitsData = produits.value.filter(p => selectedProducts.value.includes(p._id))
      
      for (const produit of selectedProduitsData) {
        const movementData = {
          produit: produit._id,
          typeMouvement: 'retour_technicien',
          quantite: -1,
          emplacementOrigine: 'chez_technicien',
          emplacementDestination: 'magasin_central',
          motif: `Retour technicien - ${commentaires.value || 'Bon de retour'}`,
          statut: 'valide',
          commentaires: commentaires.value
        }

        await fetch('/api/movements', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(movementData)
        })
      }
    }

    const generatePDF = () => {
      const doc = new jsPDF()
      const dateNow = new Date()
      const technicien = techniciens.value.find(t => t._id === selectedTechnicien.value)
      const selectedProduitsData = produits.value.filter(p => selectedProducts.value.includes(p._id))

      // EN-TÊTE
      doc.setFontSize(28)
      doc.setTextColor(0, 27, 183)
      doc.setFont('helvetica', 'bold')
      doc.text('AMANA', 105, 20, { align: 'center' })
      
      doc.setFontSize(10)
      doc.setTextColor(100, 100, 100)
      doc.setFont('helvetica', 'normal')
      doc.text('MANAGEMENT SUITE', 105, 27, { align: 'center' })
      
      doc.setDrawColor(0, 27, 183)
      doc.setLineWidth(0.5)
      doc.line(20, 32, 190, 32)

      // TITRE
      doc.setFontSize(20)
      doc.setTextColor(0, 27, 183)
      doc.setFont('helvetica', 'bold')
      doc.text('BON DE RETOUR', 105, 42, { align: 'center' })

      const bonNumber = `BR-${dateNow.getFullYear()}${String(dateNow.getMonth() + 1).padStart(2, '0')}${String(dateNow.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 9999)).padStart(4, '0')}`
      doc.setFontSize(10)
      doc.setTextColor(60, 60, 60)
      doc.text(`N° ${bonNumber}`, 105, 50, { align: 'center' })
      doc.text(`Date: ${dateNow.toLocaleDateString('fr-FR')}`, 105, 56, { align: 'center' })

      // TECHNICIEN
      let yPos = 70
      doc.setFillColor(240, 248, 255)
      doc.roundedRect(20, yPos, 170, 25, 3, 3, 'F')
      doc.setDrawColor(0, 27, 183)
      doc.roundedRect(20, yPos, 170, 25, 3, 3, 'S')
      
      doc.setFontSize(11)
      doc.setTextColor(0, 27, 183)
      doc.setFont('helvetica', 'bold')
      doc.text('TECHNICIEN', 25, yPos + 8)
      
      doc.setFontSize(10)
      doc.setTextColor(40, 40, 40)
      doc.text(`${technicien?.prenom || ''} ${technicien?.nom || ''}`, 25, yPos + 16)
      doc.text(`Email: ${technicien?.email || 'N/A'}`, 100, yPos + 16)

      // TABLEAU DES PRODUITS
      yPos = 105
      const tableData = selectedProduitsData.map((produit, index) => [
        (index + 1).toString(),
        produit.referenceInterne || '-',
        produit.designation || '-',
        produit.categorie || '-',
        '1',
        produit.imei || produit.iccid || '-'
      ])

      autoTable(doc, {
        startY: yPos,
        head: [['N°', 'Référence', 'Désignation', 'Catégorie', 'Qté', 'IMEI/ICCID']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [0, 27, 183],
          textColor: 255,
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [40, 40, 40]
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 25, halign: 'center' },
          2: { cellWidth: 50 },
          3: { cellWidth: 25, halign: 'center' },
          4: { cellWidth: 15, halign: 'center' },
          5: { cellWidth: 35, halign: 'center' }
        }
      })

      // COMMENTAIRES
      if (commentaires.value) {
        yPos = doc.lastAutoTable.finalY + 15
        doc.setFontSize(11)
        doc.setTextColor(0, 27, 183)
        doc.setFont('helvetica', 'bold')
        doc.text('COMMENTAIRES:', 20, yPos)
        
        doc.setFontSize(9)
        doc.setTextColor(60, 60, 60)
        doc.setFont('helvetica', 'normal')
        const lines = doc.splitTextToSize(commentaires.value, 170)
        doc.text(lines, 20, yPos + 8)
      }

      // SIGNATURES
      yPos = Math.max(doc.lastAutoTable?.finalY + 40 || 200, 230)
      if (yPos > 220) {
        doc.addPage()
        yPos = 30
      }

      doc.setDrawColor(16, 185, 129)
      doc.roundedRect(20, yPos, 75, 40, 3, 3, 'S')
      doc.setFontSize(10)
      doc.setTextColor(16, 185, 129)
      doc.setFont('helvetica', 'bold')
      doc.text('SIGNATURE MAGASINIER', 57.5, yPos + 8, { align: 'center' })

      doc.setDrawColor(0, 27, 183)
      doc.roundedRect(115, yPos, 75, 40, 3, 3, 'S')
      doc.setTextColor(0, 27, 183)
      doc.text('SIGNATURE TECHNICIEN', 152.5, yPos + 8, { align: 'center' })

      const fileName = `bon_retour_${technicien?.nom}_${dateNow.toISOString().split('T')[0]}.pdf`
      doc.save(fileName)
    }

    const closeModal = () => {
      emit('close')
    }

    // Watchers
    watch(selectedTechnicien, loadProduits)

    // Init
    loadTechniciens()

    return {
      selectedTechnicien,
      techniciens,
      produits,
      selectedProducts,
      commentaires,
      loading,
      exporting,
      allSelected,
      toggleSelectAll,
      exportPDF,
      closeModal
    }
  }
}
</script>

<style scoped>
.bon-retour-modal {
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.section {
  margin-bottom: 1.5rem;
}

.section h4 {
  color: var(--primary-blue);
  margin-bottom: 0.5rem;
  font-size: 1rem;
}

.form-select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
}

.products-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1rem;
}

.select-all {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;
}

.product-item {
  margin-bottom: 0.75rem;
}

.product-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s;
}

.product-checkbox:hover {
  background-color: #f8f9fa;
}

.product-info {
  flex: 1;
}

.product-main {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.designation {
  color: #666;
}

.product-details {
  display: flex;
  gap: 0.75rem;
  font-size: 0.8rem;
}

.category, .status, .imei {
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  background-color: #f1f3f4;
  color: #666;
}

textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  resize: vertical;
  font-family: inherit;
}

.loading, .no-products {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
